import { MESApiService, spParameter } from "@dexteel/mesf-core";
import { get } from "lodash-es";
import {
	Product,
	ProductAncestorRef,
	ProductChildRef,
} from "../models/Product";
import { PunchBeamPosition } from "../models/PunchBeamPosition";
import { PunchPerforation } from "../models/PunchPerforation";

export const getProducts = async (): Promise<Product[]> => {
	const apiService = new MESApiService();
	const resp = await apiService.callV2("[FAB].[GetProducts]", []);

	if (resp.ok) {
		return get(resp, "data.tables[0].rows", []) as Product[];
	}
	throw new Error(resp.message || "Error fetching products");
};

export const getProductDetail = async (
	productId: number,
	areaAssetId: number,
): Promise<Product> => {
	const apiService = new MESApiService();
	const parameters: spParameter[] = [
		{ name: "ProductId", value: productId },
		{ name: "AreaAssetId", value: areaAssetId },
	];
	const resp = await apiService.callV2("[FAB].[GetProductDetail]", parameters);

	if (resp.ok) {
		const rows = get(resp, "data.tables[0].rows", []) as Product[];
		if (rows.length > 0) {
			const children = get(
				resp,
				"data.tables[1].rows",
				[],
			) as ProductChildRef[];
			const ancestors = get(
				resp,
				"data.tables[2].rows",
				[],
			) as ProductAncestorRef[];
			const beamPositions = get(
				resp,
				"data.tables[3].rows",
				[],
			) as PunchBeamPosition[];
			const perforations = get(
				resp,
				"data.tables[4].rows",
				[],
			) as PunchPerforation[];
			const isPunch = rows[0].AreaAssetId === 50;
			const hydratedBeamPositions = isPunch
				? ensureBeamGroups(beamPositions, rows[0].ProductId ?? 0)
				: beamPositions;
			return {
				...rows[0],
				Children: children,
				Ancestors: ancestors,
				BeamPositions: hydratedBeamPositions,
				Perforations: perforations,
			};
		}
		throw new Error("Product not found");
	}
	throw new Error(resp.message || "Error fetching product detail");
};

// Ensure exactly 7 ordered positions per plate (Avenue→River). Existing rows
// are kept in their PositionOrder slot; missing slots are filled with empty
// placeholders so the form always renders a fixed grid.
function ensureBeamGroups(
	positions: PunchBeamPosition[],
	productId: number,
): PunchBeamPosition[] {
	const out: PunchBeamPosition[] = [];
	for (const Plate of ["U", "L"] as const) {
		for (let order = 1; order <= 7; order++) {
			const existing = positions.find(
				(p) => p.Plate === Plate && p.PositionOrder === order,
			);
			if (existing) {
				out.push(existing);
			} else {
				out.push({
					PunchBeamPositionId: null,
					ProductId: productId,
					Plate,
					PositionOrder: order,
					PositionValue: "",
				});
			}
		}
	}
	return out;
}

export function ensurePerforationGroups(
	perforations: PunchPerforation[],
	productId: number,
): PunchPerforation[] {
	const out = [...perforations];
	for (const PerforationType of ["HOLE", "BUTTON"] as const) {
		if (!out.some((p) => p.PerforationType === PerforationType)) {
			out.push({
				PunchPerforationId: null,
				ProductId: productId,
				PerforationType,
				SequenceOrder: 0,
				HoleSize: null,
				DetailNumber: null,
				PunchPartNumber: null,
				ButtonPartNumber: null,
			});
		}
	}
	return out;
}

export const setProductParent = async (
	childProductId: number,
	parentProductId: number | null,
) => {
	const apiService = new MESApiService();
	const parameters: spParameter[] = [
		{ name: "ChildProductId", value: childProductId },
		{ name: "ParentProductId", value: parentProductId },
	];
	return await apiService.callV2("[FAB].[SetProductParent]", parameters);
};

export const createProduct = async (product: Partial<Product>) => {
	const apiService = new MESApiService();
	const parameters: spParameter[] = [
		{ name: "ProductId", value: null },
		{ name: "PartNumber", value: product.PartNumber ?? null },
		{
			name: "ExtendedPartNumber",
			value: product.ExtendedPartNumber ?? null,
		},
		{ name: "CustomerPartNumber", value: product.CustomerPartNumber ?? null },
		{ name: "GenericName", value: product.GenericName ?? null },
		{ name: "CustomerId", value: product.CustomerId ?? null },
		{ name: "SectionId", value: product.SectionId ?? null },
		{ name: "GradeId", value: product.GradeId ?? null },
		{ name: "AreaAssetId", value: product.AreaAssetId ?? null },
		{ name: "IsActive", value: product.IsActive ?? null },
		{ name: "Comments", value: product.Comments ?? null },
	];
	return await apiService.callV2("[FAB].[UpsertProduct]", parameters);
};

export const updateProduct = async (
	productId: number,
	fields: Record<string, string | number | boolean | null>,
) => {
	const apiService = new MESApiService();
	const parameters: spParameter[] = [{ name: "ProductId", value: productId }];

	for (const [field, value] of Object.entries(fields)) {
		parameters.push({ name: field, value: value });
	}

	return await apiService.callV2("[FAB].[UpsertProduct]", parameters);
};

export const deleteProduct = async (productId: number) => {
	const apiService = new MESApiService();
	const parameters: spParameter[] = [{ name: "ProductId", value: productId }];
	return await apiService.callV2("[FAB].[DeleteProduct]", parameters);
};
